import classNames from 'classnames';
import DOMPurify from 'dompurify';
import isHtml from 'is-html';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo } from 'react';
import type { ColorType, TextType } from '../../types/component.type';
import './text.component.scss';

const htmlTagsStyles = {
  u: { textDecoration: 'underline' },
  b: { fontWeight: 'bold' },
  s: { textDecoration: 'line-through' },
  br: {},
} as const satisfies Record<string, React.CSSProperties>;

export declare namespace TextType {
  type Props = {
    children?: React.ReactNode;
    className?: string | null;
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    variant?: TextType;
    color?: ColorType;
    whiteSpace?: 'nowrap' | 'wrap' | 'wrap-reverse' | 'break-spaces';
    wordBreak?: 'break-all' | 'break-word' | 'keep-all';
    textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
    textDecoration?: 'underline';
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    textOverflow?: 'ellipsis';
    textOverflowLine?: number;
    allowedHtmlTags?: (keyof typeof htmlTagsStyles)[];
  };
}

export const Text = observer(
  ({
    children,
    className,
    onClick,
    variant = 'body',
    color,
    textTransform,
    textDecoration,
    whiteSpace = 'wrap',
    textAlign = 'left',
    wordBreak,
    textOverflow,
    textOverflowLine,
    allowedHtmlTags = ['u', 'b', 'br', 's'],
  }: TextType.Props) => {
    const width = useMemo(
      () => (textOverflow ? '100%' : 'initial'),
      [textOverflow],
    );

    const overflow = useMemo(
      () => (textOverflow ? 'hidden' : 'initial'),
      [textOverflow],
    );

    const multiLineEllipsisStyle = useMemo((): React.CSSProperties => {
      if (
        textOverflow === 'ellipsis' &&
        textOverflowLine &&
        textOverflowLine > 1
      ) {
        return {
          display: '-webkit-box',
          WebkitLineClamp: textOverflowLine,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
        };
      }
      return {};
    }, [textOverflow, textOverflowLine]);

    // Détection et sanitization automatique de l'HTML
    const { isHtmlContent, sanitizedHtml } = useMemo(() => {
      if (typeof children !== 'string') {
        return { isHtmlContent: false, sanitizedHtml: '' };
      }

      const isHtmlDetected = isHtml(children);

      if (isHtmlDetected) {
        // Convertir les styles CSS en string pour chaque tag
        const cssStylesMap: Record<string, string> = {};
        Object.entries(htmlTagsStyles).forEach(([tag, styles]) => {
          cssStylesMap[tag.toLowerCase()] = Object.entries(styles)
            .map(([prop, value]) => {
              // Convertir camelCase en kebab-case
              const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
              return `${cssProp}: ${value}`;
            })
            .join('; ');
        });

        // Hook DOMPurify pour ajouter les styles inline
        DOMPurify.addHook('afterSanitizeAttributes', (node) => {
          const tagName = node.tagName?.toLowerCase();
          if (tagName && cssStylesMap[tagName]) {
            const existingStyle = node.getAttribute('style') || '';
            const newStyle = existingStyle
              ? `${existingStyle}; ${cssStylesMap[tagName]}`
              : cssStylesMap[tagName];
            node.setAttribute('style', newStyle);
          }
        });

        const cleaned = DOMPurify.sanitize(children, {
          ALLOWED_TAGS: allowedHtmlTags,
          ALLOWED_ATTR: ['style'],
        });

        // Retirer le hook après utilisation
        DOMPurify.removeAllHooks();

        return { isHtmlContent: true, sanitizedHtml: cleaned };
      }

      return { isHtmlContent: false, sanitizedHtml: '' };
    }, [children, allowedHtmlTags, htmlTagsStyles]);

    const childrenFormatted =
      typeof children === 'string' ? children.trim() : children;

    // Helper pour rendre le contenu (HTML ou texte normal)
    const renderContent = useCallback(
      (Tag: keyof JSX.IntrinsicElements) => {
        const commonProps = {
          translate: 'no' as const,
          style: { width, overflow, textOverflow, ...multiLineEllipsisStyle },
          className: 'text__item',
        };

        if (isHtmlContent && sanitizedHtml) {
          return (
            <Tag
              {...commonProps}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          );
        }

        return <Tag {...commonProps}>{childrenFormatted}</Tag>;
      },
      [
        width,
        overflow,
        textOverflow,
        multiLineEllipsisStyle,
        isHtmlContent,
        sanitizedHtml,
        childrenFormatted,
      ],
    );

    return (
      <div
        className={classNames(
          'text',
          `text--variant--${variant}`,
          `text--color--${color}`,
          className,
        )}
        onClick={(e) => {
          onClick?.(e);
        }}
        style={{
          textTransform,
          whiteSpace,
          textAlign,
          textDecoration,
          wordBreak,
          width,
        }}
      >
        {['x-large-title'].includes(variant) && renderContent('h1')}
        {['large-title'].includes(variant) && renderContent('h2')}
        {['title-1'].includes(variant) && renderContent('h3')}
        {['title-2'].includes(variant) && renderContent('h4')}
        {['title-3'].includes(variant) && renderContent('h5')}
        {['headline'].includes(variant) && renderContent('h6')}
        {[
          'button-m',
          'button-s',
          'subhead',
          'link',
          'label',
          'sublabel',
          'headcaption',
          'caption',
        ].includes(variant) && renderContent('span')}
        {['body'].includes(variant) && renderContent('p')}
      </div>
    );
  },
);
